import React, { useEffect } from "react";
import { Box, Drawer, IconButton, Tooltip, Typography, useMediaQuery, useTheme } from "@mui/material";
import { MdDashboard, MdAnalytics, MdKeyboardArrowLeft, MdKeyboardArrowRight, MdGroups, MdCampaign } from "react-icons/md";
import { RiNumbersLine } from "react-icons/ri";
import { BiRupee, BiLogIn } from "react-icons/bi";
import { IoSwapHorizontal, IoWallet, IoImages } from "react-icons/io5";
import { GiCardboardBoxClosed } from "react-icons/gi";
import { CgScreen } from "react-icons/cg";
import { NavLink, useLocation } from "react-router-dom";
import { setCurrentDashboardName } from "../../redux/Actions/loginActions";
import { useDispatch } from "react-redux";
import { useLayoutContext } from "../../context/LayoutContext";
import { getOnboardingAccess } from "../../features/sellerOnboarding/api";

export const navigationItems = [
  { to: "/dashboard", icon: MdDashboard, title: "Dashboard" },
  { to: "/vendors", icon: MdGroups, title: "Vendors" },
  { to: "/onboarding", icon: MdGroups, title: "Onboarding Review" },
  { to: "/analytics", icon: MdAnalytics, title: "Analytics" },
  { to: "/listed-products", icon: GiCardboardBoxClosed, title: "Products" },
  { to: "/QManagement", icon: RiNumbersLine, title: "Quantity Management" },
  { to: "/pricing", icon: BiRupee, title: "Pricing Profit" },
  { to: "/pricing/catalog", icon: BiRupee, title: "Catalog Pricing" },
  { to: "/orders", icon: IoSwapHorizontal, title: "Orders" },
  { to: "/wallet", icon: IoWallet, title: "Wallet" },
  { to: "/remmitance", icon: BiRupee, title: "Remittance" },
  { to: "/signup", icon: BiLogIn, title: "Create User" },
  { to: "/edit-upload", icon: IoImages, title: "Edit Upload" },
  { to: "/banner", icon: CgScreen, title: "Banners" },
  { to: "/leads", icon: MdGroups, title: "Leads" },
  { to: "/leads/ads-tracking", icon: MdCampaign, title: "Ads Tracking" },
];


function NavigationContent({ expanded, onNavigate, items }) {
  return (
    <Box
      component="nav"
      aria-label="Admin navigation"
      sx={{
        display: "flex",
        flexDirection: "column",

        // Increased gap only for collapsed sidebar
        gap: expanded ? 0.75 : 2.5,
        px: expanded ? 1.25 : 1.5,
        py: expanded ? 1 : 1.6,
        alignItems: expanded ? "stretch" : "center",
        width: "100%",
      }}
    >
      {items.map(({ to, icon: Icon, title }) => {
        const item = (
          <NavLink
            key={to}
            to={to}
            end={to === "/leads" || to === "/pricing"}
            onClick={onNavigate}
            className={({ isActive }) =>
              `adminNavLink${expanded ? "" : " adminNavLinkCompact"}${isActive ? " adminNavLinkActive" : ""
              }`
            }
          >
            <Icon className="adminNavIcon" aria-hidden="true" />
            {expanded && <span>{title}</span>}
          </NavLink>
        );

        return expanded ? (
          item
        ) : (
          <Tooltip key={to} title={title} placement="right">
            {item}
          </Tooltip>
        );
      })}
    </Box>
  );
}
export default function Sidebar() {
  const location = useLocation();
  const dispatch = useDispatch();
  const theme = useTheme();
  const mobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { sidebarOpen, setSidebarOpen, mobileDrawerOpen, setMobileDrawerOpen } = useLayoutContext();
  const [canViewVendors, setCanViewVendors] = React.useState(false);
  const [canViewOnboarding, setCanViewOnboarding] = React.useState(false);

  useEffect(() => { dispatch(setCurrentDashboardName(navigationItems.map(({ to, title }) => ({ to, title })).sort((a, b) => b.to.length - a.to.length))); }, [dispatch, location.pathname]);
  useEffect(() => { setMobileDrawerOpen(false); }, [location.pathname, setMobileDrawerOpen]);
  useEffect(() => {
    let active = true;
    getOnboardingAccess().then(({ data }) => {
      if (!active) return;
      setCanViewVendors(data.permissions?.includes("vendors_view"));
      setCanViewOnboarding(data.permissions?.includes("onboarding_view"));
    })
      .catch(() => active && setCanViewVendors(false));
    return () => { active = false; };
  }, []);
  const visibleItems = navigationItems.filter((item) =>
    (item.to !== "/vendors" || canViewVendors) && (item.to !== "/onboarding" || canViewOnboarding));

  const panel = (
    <Box sx={{ height: "100%", overflowY: "auto", color: "#fff", bgcolor: "#24213f", backgroundImage: "linear-gradient(180deg,#29254c 0%,#1f1d37 100%)", pt: 1 }}>
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: sidebarOpen || mobile ? "space-between" : "center", px: 1.5, pb: 1.25, minHeight: 46 }}>
        {(sidebarOpen || mobile) && <Box><Typography sx={{ fontSize: 11, fontWeight: 800, letterSpacing: ".14em", color: "#a9a5c9" }}>WORKSPACE</Typography><Typography sx={{ fontSize: 14, fontWeight: 750 }}>Seller Operations</Typography></Box>}
        {!mobile && (
          <Tooltip title={sidebarOpen ? "Collapse navigation" : "Expand navigation"}>
            <IconButton
              aria-label={sidebarOpen ? "Collapse navigation" : "Expand navigation"}
              onClick={() => {
                if (sidebarOpen) {
                  setSidebarOpen(false);
                } else {
                  setSidebarOpen(true);
                }
              }}
              sx={{
                color: "#d8d5ef",
                width: 38,
                height: 38,
                "&:hover": { bgcolor: "rgba(255,255,255,.08)" },
              }}
            >
              {sidebarOpen ? <MdKeyboardArrowLeft /> : <MdKeyboardArrowRight />}
            </IconButton>
          </Tooltip>
        )}
      </Box>
      <NavigationContent
        items={visibleItems}
        expanded={mobile || sidebarOpen}
        onNavigate={() => {
          if (mobile) {
            setMobileDrawerOpen(false);
          }
        }}
      />
    </Box>
  );

  if (mobile) return <Drawer open={mobileDrawerOpen} onClose={() => setMobileDrawerOpen(false)} ModalProps={{ keepMounted: true }} PaperProps={{ sx: { width: "min(86vw, 304px)", top: 60, height: "calc(100% - 60px)", border: 0 } }}>{panel}</Drawer>;

  return (
    <Box component="aside" sx={{ width: sidebarOpen ? 246 : 72, flexShrink: 0, minHeight: "calc(100vh - 64px)", transition: "width .22s ease" }}>
      <Box sx={{ position: "sticky", top: 64, height: "calc(100vh - 64px)" }}>{panel}</Box>
    </Box>
  );
}
